import type { ReactNode } from "react";
import { Typography } from "@circle-react-uikit/Typography";
import { ChartContainer } from "../shared/ChartContainer";
import { LoadingChartComponent } from "../shared/LoadingChartComponent";
import { TrendDelta } from "./TrendDelta";

interface OverviewStatComponentProps {
  children?: ReactNode;
  className?: string;
  comparisonDeltaPercentage?: number;
  comparisonLastPeriodValue?: number | string;
  decimalPlaces?: number;
  isLoading?: boolean;
  prefix?: string;
  // Layout-only: reserve the space for TrendDelta even if not shown
  reserveTrendSpace?: boolean;
  // Comparison (trend) presentation
  showComparison?: boolean;
  suffix?: string;
  title: string;
  titleLabel?: string;
  tooltip?: string;
  value: number | string;
}

const formatStatValue = (
  value: number | string,
  decimalPlaces: number,
): string => {
  if (typeof value === "string") {
    return value || "—";
  }

  return value.toLocaleString(undefined, {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  });
};

export const OverviewStatComponent = ({
  prefix,
  suffix,
  title,
  tooltip,
  titleLabel,
  value,
  isLoading,
  className,
  decimalPlaces = 0,
  children,
  showComparison,
  comparisonDeltaPercentage,
  comparisonLastPeriodValue,
  reserveTrendSpace,
}: OverviewStatComponentProps) => {
  if (isLoading) {
    return <LoadingChartComponent type="stat" className={className} />;
  }

  const formattedValue = formatStatValue(value, decimalPlaces);
  const shouldShowTrend =
    Boolean(showComparison) && typeof comparisonDeltaPercentage === "number";

  return (
    <ChartContainer
      title={title}
      tooltip={tooltip}
      className={className}
      titleLabel={titleLabel}
    >
      <Typography.TitleLg as="div" weight="font-bold" color="text-very-dark">
        {prefix}
        {formattedValue}
        {suffix}
      </Typography.TitleLg>
      {shouldShowTrend && (
        <div className="mt-0.5 inline-flex items-center whitespace-nowrap">
          <TrendDelta
            deltaPercentage={comparisonDeltaPercentage}
            withBackground
            lastPeriodValue={
              comparisonLastPeriodValue !== ""
                ? comparisonLastPeriodValue
                : undefined
            }
            suffix={suffix}
          />
        </div>
      )}
      {!shouldShowTrend && reserveTrendSpace && (
        <div className="mt-0.5 inline-flex h-[22px] items-center whitespace-nowrap" />
      )}
      {children}
    </ChartContainer>
  );
};
